import * as React from 'react';
import { Alert, Col, Button, ButtonToolbar } from 'react-bootstrap';

import { Organization, Permission, User } from '../gs-core/common/models';

import './DocView.css';
import { OwnerSection } from './Section/OwnerSection';
import { SubjectAndTitle } from './SubjectAndTitle/SubjectAndTitle';
import { PermissionsSection } from './Section/PermissionsSection';
import { Viewer } from './Viewer/Viewer';
import {
  DEFAULT_PAGE_SIZE, Doc, DocStatuses, FilterParams, Subject, ViewAccess,
  ViewAccessTypes
} from '../common/models';
import { Pagination } from '../Pagination/Pagination';
import { NotesSection } from './Section/NotesSection';

interface DocViewProps {
  currentUser: User;
  permissions: Permission[];
  filters?: FilterParams;
  doc: Doc;
  page?: number;
  total?: number;
  editing: boolean;
  saving: boolean;
  canEdit: boolean;
  onSelectSubject: (subject: Subject | undefined) => void;
  onChangeOwningUnit: (owningUnit: Organization) => void;
  onSelectTitle: (title: string) => void;
  onChangeViewAccess: (viewAccess: ViewAccess) => void;
  onChangeNotes: (notes: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onEdit: () => void;
  onChangePage: (page: number) => void;
}

interface DocViewState {}

export class DocView extends React.Component<DocViewProps, DocViewState> {

  onKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { canEdit, editing, doc, onChangeViewAccess, page = 1, total = 1, onChangePage } = this.props;
    const { key, ctrlKey, shiftKey } = event;
    const tagName = (event.target as HTMLInputElement).tagName;

    if (editing) {
      if (key === 'A' && ctrlKey && shiftKey) {
        const viewAccessTypes = ViewAccessTypes.filterBySubject(doc.subject);
        const lastOptions = viewAccessTypes.slice(-1)[0];
        const newViewAccess = doc.viewAccess === lastOptions ? viewAccessTypes.slice(-2)[0] : lastOptions;

        onChangeViewAccess(newViewAccess);
      } else if (key === 's' && ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        if (this.canSave()) {
          this.props.onSave();
        }
      } else if (key === 'Escape' && !['INPUT', 'TEXTAREA'].includes(tagName)) {
        this.props.onCancel();
      }
    } else {
      if (canEdit && key === 'e') {
        this.props.onEdit();
      } else if (key === 'ArrowRight' && page < total) {
        onChangePage(page + 1);
      } else if (key === 'ArrowLeft' && page > 1) {
        onChangePage(page - 1);
      }
    }

    event.persist();
  };

  canSave = (): boolean => {
    return !this.props.saving && !!this.props.doc.title && !!this.props.doc.subject;
  };

  render() {
    const {
      currentUser, permissions, filters, doc, page, total, editing, saving, canEdit, onSelectSubject,
      onChangeOwningUnit, onSelectTitle, onChangeViewAccess, onChangeNotes, onSave, onCancel, onDelete,
      onRestore, onEdit, onChangePage
    } = this.props;
    const deleteDate = doc.deleteDate as Date;
    let listUrl = filters && filters.toDocListUrl();

    if (listUrl && page) {
      listUrl += ( listUrl.includes('?') ? '&' : '?' ) + 'page=' + Math.ceil(page / DEFAULT_PAGE_SIZE);
    }

    return (
      <div className="DocView panel panel-default" onKeyDown={this.onKey} tabIndex={0}>
        {doc.status === DocStatuses.DELETED &&
          <div className="panel-heading text-center">
            <Alert bsStyle="danger" className="text-center delete-message">
              Deleted on {deleteDate.toDateString()} at {deleteDate.toLocaleTimeString()}<br />
              <span className="text-muted">purged after 30 days</span>
            </Alert>
          </div>
        }
        <div className="panel-body panel-body--fill-entire">
          <div className="doc-container">
            <Col md={8} className="viewer-container">
              <Viewer key={doc.url} url={doc.url as string}/>
            </Col>
            <Col md={4} className="sidebar-container">
              <SubjectAndTitle
                doc={doc}
                editing={editing}
                onSelectSubject={onSelectSubject}
                onSelectTitle={onSelectTitle}
              />
              {doc.subject &&
                <PermissionsSection
                  currentUser={currentUser}
                  permissions={permissions}
                  doc={doc}
                  editing={editing}
                  onChangeViewAccess={onChangeViewAccess}
                />
              }

              <OwnerSection
                currentUser={currentUser}
                permissions={permissions}
                doc={doc}
                editing={editing}
                onChangeOwningUnit={onChangeOwningUnit}
              />

              <NotesSection notes={doc.details} editing={editing} onChange={onChangeNotes}/>

              <div className="controls">
                {doc.status !== DocStatuses.DELETED && (
                  editing ? (
                    <ButtonToolbar>
                      <Button
                        bsStyle="primary"
                        onClick={onSave}
                        disabled={!this.canSave()}
                        title="Type 'ctrl s'"
                      >
                        {saving ? 'Saving' : 'Save'}
                      </Button>
                      <Button onClick={onCancel} disabled={saving} title="Type 'Esc'">Cancel</Button>
                      <Button onClick={onDelete} bsStyle="danger" className="pull-right" disabled={saving}>
                        <i className="fa fa-trash-o"/>{' '}Delete
                      </Button>
                    </ButtonToolbar>
                  ) : (
                    canEdit && <Button onClick={onEdit} title="Type 'e'" autoFocus={true}>
                      <i className="fa fa-edit"/>{' '}Edit
                    </Button>
                  )
                )}
                {canEdit && doc.status === DocStatuses.DELETED &&
                  <Button onClick={onRestore} bsStyle="success" block={true} disabled={saving}>
                    <i className="fa fa-undo"/>{' '}Restore
                  </Button>
                }
                {!!page && !!total &&
                  <Pagination
                    page={page}
                    pageSize={1}
                    total={total}
                    listUrl={listUrl}
                    keyNavigation={!editing}
                    onChangePage={onChangePage}
                  />
                }
                { editing && doc.status !== DocStatuses.DELETED ? (
                  <div className="help-block">
                    <b>Note:</b> eDocs is intended for uploading academic and postdoc records only.
                    No employment / personnel documents, personal financial records, government issued
                    identification or medical records.
                  </div> ) : ''
                }
              </div>
            </Col>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/DocView/DocView.tsx